/**
 * Customer authentication API + local state (SEPARATE from admin/apiClient).
 *
 * Authoritative session is the httpOnly `session_token` cookie set by the
 * backend on register / login / session. We also mirror the token to
 * localStorage so we can send it as an `Authorization: Bearer` header for
 * clients whose browser strips 3rd-party cookies; the cookie remains
 * authoritative on the server.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BASE = `${BACKEND}/api/customer/auth`;

// Some browsers strip 3rd-party cookies. Keep the token in localStorage so
// we can also send it as `Authorization: Bearer` — the cookie stays authoritative.
const TOKEN_KEY = "ff_customer_token";
export const customerTokenStore = {
  get: () => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  },
  set: (t) => { try { localStorage.setItem(TOKEN_KEY, t); } catch {} },
  clear: () => { try { localStorage.removeItem(TOKEN_KEY); } catch {} },
};

const client = axios.create({ baseURL: BASE, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = customerTokenStore.get();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Backend can also return the token in the response body for header fallback.
function persistIfReturned(data) {
  if (data && typeof data === "object" && data.session_token) {
    customerTokenStore.set(data.session_token);
  }
  return data;
}

export const customerAuthApi = {
  me: () => client.get("/me").then((r) => r.data),
  register: (body) => client.post("/register", body).then((r) => persistIfReturned(r.data)),
  login: (email, password) =>
    client.post("/login", { email, password }).then((r) => persistIfReturned(r.data)),
  google: (session_id) =>
    client.post("/session", { session_id }).then((r) => persistIfReturned(r.data)),
  logout: () =>
    client.post("/logout").finally(() => customerTokenStore.clear()),
};

/**
 * Redirect the browser to Emergent Google OAuth. After Google auth, the user
 * lands back on `${window.location.origin}` with `#session_id=...` in the URL
 * fragment, which our AuthCallback handler picks up.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export function startGoogleAuth(returnTo = "/") {
  const redirectUrl = window.location.origin + returnTo;
  window.location.href =
    `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}
